import { isAddress, isHex } from 'viem';
import { InvalidAddressError, InvalidHexError } from '../errors.mjs';
import keccak256 from 'keccak256';
import { NULL_ADDRESS } from './constants.mjs';

const addressCheckSet = /* @__PURE__ */ new Set();
function assertAddress(address, {
  type = "GENERIC"
} = {}) {
  if (addressCheckSet.has(address))
    return;
  if (isAddress(address)) {
    addressCheckSet.add(address);
    return;
  }
  throw new InvalidAddressError({ address, type });
}
function assertIsHex(value) {
  if (!isHex(value, { strict: false }))
    throw new InvalidHexError({ value });
}
function validateAddress(address, {
  type,
  notNull
}) {
  if (notNull) {
    if (address === NULL_ADDRESS) {
      throw new InvalidAddressError({ type, address, message: "Address cannot be null address" });
    }
  } else if (address === NULL_ADDRESS)
    return;
  assertAddress(address, { type });
}
function getSaltAsBytes32(salt) {
  return `0x${keccak256(salt.toString()).toString("hex")}`;
}
function getLazyOption(option) {
  return typeof option === "function" ? option() : option;
}
function scaleAmount({ amount, multiplier }) {
  return amount * BigInt(multiplier * 100) / 100n;
}
function parseTokenIdNumber(str) {
  const parsedInt = parseInt(str, 10);
  return isNaN(parsedInt) ? null : parsedInt;
}
function curry(fn) {
  const curried = (...args) => {
    if (args.length >= fn.length) {
      return fn(...args);
    }
    return curry((...args2) => fn(...args, ...args2));
  };
  return curried;
}
function exhaustiveGuard(_value) {
  throw new Error(`Exhaustive guard reached with value ${_value}`);
}
async function retry(fn, {
  attempts,
  delay
}) {
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (error) {
      if (i < attempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      } else {
        throw error;
      }
    }
  }
  return fn();
}
function isHexList(list) {
  return list.every((value) => isHex(value, { strict: false }));
}
function assertIsHexList(list) {
  for (const value of list) {
    assertIsHex(value);
  }
}
function BigIntMin(...[firstValue, ...values]) {
  let value = firstValue;
  for (const currentValue of values) {
    if (currentValue < value)
      value = currentValue;
  }
  return value;
}
function BigIntMax(...[firstValue, ...values]) {
  let value = firstValue;
  for (const currentValue of values) {
    if (currentValue > value)
      value = currentValue;
  }
  return value;
}

export { BigIntMax, BigIntMin, assertAddress, assertIsHex, assertIsHexList, curry, exhaustiveGuard, getLazyOption, getSaltAsBytes32, isHexList, parseTokenIdNumber, retry, scaleAmount, validateAddress };
